import { useState } from "react";
import {
  Search,
  SlidersHorizontal,
  ChevronRight,
  Check,
  X,
} from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { cn } from "../../lib/utils";
import { EventName, trackEvent } from "../../lib/metrics";

// One filter dimension in the popover: a category the user drills into, and
// the multi-select options inside it.
export interface FilterCategory {
  id: string;
  label: string;
  options: Array<{ label: string; value: string }>;
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
}

interface EntityFilterBarProps {
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  searchPlaceholder: string;
  // Built by the caller from its own entity's filter options — the only part
  // of this bar that differs per entity.
  categories: FilterCategory[];
  isFiltersOpen: boolean;
  onFiltersOpenChange: (open: boolean) => void;
  activeFilterCount: number;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  // Which list this bar filters; reported on filter events.
  entityType: "controls" | "work_steps";
}

// Search box + two-pane filter popover (pick a category, drill into its
// values) + the selected-value chips beneath. Entity-agnostic so every audit
// list page shares one drill-in interaction.
export const EntityFilterBar = ({
  searchTerm,
  onSearchTermChange,
  searchPlaceholder,
  categories: filterCategories,
  isFiltersOpen,
  onFiltersOpenChange,
  activeFilterCount,
  hasActiveFilters,
  onClearFilters,
  entityType,
}: EntityFilterBarProps) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [categorySearch, setCategorySearch] = useState("");
  const [optionSearch, setOptionSearch] = useState("");
  // Get the currently selected category object
  const currentCategory = filterCategories.find(
    (cat) => cat.id === selectedCategory,
  );

  // Filter categories based on search
  const filteredCategories = filterCategories.filter((cat) =>
    cat.label.toLowerCase().includes(categorySearch.toLowerCase()),
  );

  // Filter options based on search
  const filteredOptions = currentCategory?.options.filter((opt) =>
    opt.label.toLowerCase().includes(optionSearch.toLowerCase()),
  );

  const handleOptionToggle = (value: string) => {
    if (!currentCategory) return;

    const isSelected = currentCategory.selectedValues.includes(value);
    if (isSelected) {
      currentCategory.onSelectionChange(
        currentCategory.selectedValues.filter((v) => v !== value),
      );
    } else {
      currentCategory.onSelectionChange([
        ...currentCategory.selectedValues,
        value,
      ]);
      // Category key only: option values can be customer data.
      trackEvent(EventName.CreateTableFilter, {
        entityType,
        filterKey: currentCategory.id,
      });
    }
  };

  const handleOpenChange = (open: boolean) => {
    onFiltersOpenChange(open);
    if (!open) {
      setSelectedCategory(null);
      setCategorySearch("");
      setOptionSearch("");
    }
  };

  return (
    <div className="flex items-center gap-2 mb-4">
      <Popover open={isFiltersOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className={cn(
              "shrink-0",
              activeFilterCount > 0 && "border-stone-900",
            )}
          >
            <SlidersHorizontal className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[500px] p-0" align="start" sideOffset={8}>
          <div className="flex h-[350px]">
            {/* Left panel - Categories */}
            <Command className="w-1/2 border-r">
              <CommandInput
                placeholder="Filter..."
                value={categorySearch}
                onValueChange={setCategorySearch}
              />
              <CommandList>
                <CommandEmpty>No categories found</CommandEmpty>
                <CommandGroup>
                  {filteredCategories.map((category) => {
                    const hasSelection = category.selectedValues.length > 0;
                    return (
                      <CommandItem
                        key={category.id}
                        value={category.label}
                        onMouseEnter={() => {
                          setSelectedCategory(category.id);
                          setOptionSearch("");
                        }}
                        className={cn(
                          "flex items-center justify-between cursor-pointer",
                          selectedCategory === category.id && "bg-stone-100",
                        )}
                      >
                        <span className={cn(hasSelection && "font-medium")}>
                          {category.label}
                          {hasSelection && (
                            <span className="ml-1.5 text-xs text-stone-500">
                              ({category.selectedValues.length})
                            </span>
                          )}
                        </span>
                        <ChevronRight className="h-4 w-4 text-stone-400" />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>

            {/* Right panel - Options */}
            <Command className="w-1/2">
              <CommandInput
                placeholder="Filter..."
                value={optionSearch}
                onValueChange={setOptionSearch}
              />
              <CommandList>
                {!currentCategory ? (
                  <div className="py-6 text-center text-sm text-stone-500">
                    Select a category
                  </div>
                ) : filteredOptions && filteredOptions.length > 0 ? (
                  <CommandGroup>
                    {filteredOptions.map((option) => {
                      const isSelected =
                        currentCategory.selectedValues.includes(option.value);
                      return (
                        <CommandItem
                          key={option.value}
                          value={option.label}
                          onSelect={() => handleOptionToggle(option.value)}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <div
                            className={cn(
                              "flex h-4 w-4 items-center justify-center rounded-sm border border-stone-300",
                              isSelected && "bg-stone-900 border-stone-900",
                            )}
                          >
                            {isSelected && (
                              <Check className="h-3 w-3 text-white" />
                            )}
                          </div>
                          <span>{option.label}</span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ) : (
                  <CommandEmpty>No options found</CommandEmpty>
                )}
              </CommandList>
            </Command>
          </div>
        </PopoverContent>
      </Popover>

      {/* Search Input */}
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-stone-400" />
        <Input
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Clear filters button */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearFilters}
          className="shrink-0 gap-1 text-stone-500 hover:text-stone-900"
        >
          <X className="h-4 w-4" />
          Clear
        </Button>
      )}
    </div>
  );
};
